import { FC, UIEvent, useMemo, useState } from 'react'
import en from 'src/constants/en'
import { LiveNotificationItem } from 'src/components/LiveNotificationItem'
import { Type } from 'src/components/LiveNotificationItem/enums/type.enum'
import { useGetInformationTooltipsQuery } from 'src/reactQuery/useGetInformationTooltips'
import {
  AnalyticsFetchThreadsResponseDataItem,
  AnalyticsFetchThreadsSentimentType,
} from 'src/services/__generated__/api'
import { SectionTitle } from 'src/components/SectionTitle'
import { Skeleton } from 'src/components/Skeleton'
import styles from './liveNotifications.module.scss'

// Mirrors LiveNotificationItem (liveNotificationItem.module.scss): 12px vertical
// padding + 24px bottom margin around a title row (16px line + 8px margin), a
// campaign line (15px line + 5px margin) and the text line (19px) — ~111px per
// row. The scroll area is $live-notification-container-height (409px) minus the
// tab strip, so ~4 rows fill it without inventing scrollback.
const NOTIFICATION_SKELETON_COUNT = 4

interface LiveNotificationsSectionProps {
  handleNotificationsScroll: (
    event: UIEvent,
    sentiment: AnalyticsFetchThreadsSentimentType
  ) => void
  isLoading?: boolean
  negativeData: AnalyticsFetchThreadsResponseDataItem[] | null
  neutralData: AnalyticsFetchThreadsResponseDataItem[] | null
  positiveData: AnalyticsFetchThreadsResponseDataItem[] | null
}

export const LiveNotificationsSection: FC<LiveNotificationsSectionProps> = ({
  handleNotificationsScroll,
  isLoading,
  negativeData,
  neutralData,
  positiveData,
}) => {
  const { data: informationTooltips } = useGetInformationTooltipsQuery()

  const [activeTitle, setActiveTitle] = useState('All')

  // First load only — once any sentiment bucket has arrived, a refetch (e.g. the
  // auto-refresh poll) keeps the real rows on screen rather than flashing back
  // to placeholders.
  const isInitialLoading =
    !!isLoading && !positiveData && !neutralData && !negativeData

  const getDataToRender = () => {
    switch (activeTitle) {
      case 'Positive':
        return [...(positiveData || [])]
      case 'Neutral':
        return [...(neutralData || [])]
      case 'Negative':
        return [...(negativeData || [])]
      case 'All':
      default: {
        const combined = [
          ...(positiveData || []),
          ...(neutralData || []),
          ...(negativeData || []),
        ]
        return combined.sort((a, b) => {
          const dateA = a.published_at ? new Date(a.published_at).getTime() : 0
          const dateB = b.published_at ? new Date(b.published_at).getTime() : 0
          return dateB - dateA
        })
      }
    }
  }

  const dataToRender = useMemo(
    () => getDataToRender(),
    [activeTitle, positiveData, neutralData, negativeData]
  )

  const determineType = (activeTitle: string): Type => {
    switch (activeTitle) {
      case 'Positive':
        return Type.POSITIVE
      case 'Negative':
        return Type.NEGATIVE
      case 'Neutral':
        return Type.NEUTRAL
      case 'All':
      default:
        return Type.OTHER
    }
  }

  const determineSentiment = (
    activeTitle: string
  ): AnalyticsFetchThreadsSentimentType => {
    switch (activeTitle) {
      case 'Positive':
        return AnalyticsFetchThreadsSentimentType.POS
      case 'Negative':
        return AnalyticsFetchThreadsSentimentType.NEG
      case 'Neutral':
        return AnalyticsFetchThreadsSentimentType.NEU
      case 'All':
      default:
        return AnalyticsFetchThreadsSentimentType.POS
    }
  }

  return (
    <div className={styles.liveNotifications}>
      <SectionTitle
        isLoading={isLoading}
        liveIcon
        title={en.liveNotifications}
        tooltipContent={informationTooltips?.liveNotifications}
      />

      <div className={styles.innerContainer}>
        <div className={styles.notificationsContainer}>
          <div className={styles.notificationsContainerTitleWrapper}>
            <span
              className={
                activeTitle === 'All'
                  ? styles.notificationsContainerTitleActive
                  : styles.notificationsContainerTitle
              }
              onClick={() => setActiveTitle('All')}
            >
              All
            </span>
            <span
              className={
                activeTitle === 'Positive'
                  ? styles.notificationsContainerTitleActive
                  : styles.notificationsContainerTitle
              }
              onClick={() => setActiveTitle('Positive')}
            >
              Positive
            </span>
            <span
              className={
                activeTitle === 'Neutral'
                  ? styles.notificationsContainerTitleActive
                  : styles.notificationsContainerTitle
              }
              onClick={() => setActiveTitle('Neutral')}
            >
              Neutral
            </span>
            <span
              className={
                activeTitle === 'Negative'
                  ? styles.notificationsContainerTitleActive
                  : styles.notificationsContainerTitle
              }
              onClick={() => setActiveTitle('Negative')}
            >
              Negative
            </span>
          </div>

          <div
            className={styles.scrollbarContainer}
            onScroll={(e) =>
              handleNotificationsScroll(e, determineSentiment(activeTitle))
            }
          >
            {isInitialLoading &&
              Array.from({ length: NOTIFICATION_SKELETON_COUNT }).map(
                (_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={styles.skeletonItem} key={index}>
                    <div className={styles.skeletonTitleRow}>
                      <Skeleton height={16} radius={4} width={180} />
                      <Skeleton height={20} radius={5} width={32} />
                    </div>
                    <Skeleton height={15} radius={4} width={120} />
                    <Skeleton height={19} radius={4} width="100%" />
                  </div>
                )
              )}
            {dataToRender?.map((item, index) => (
              <LiveNotificationItem
                campaign={item.campaign_name}
                hideCheckboxOnHover
                key={index}
                keyword={item.keyword}
                language={item.language}
                link={item.link}
                originalLangCode={item.original_lang_code}
                originalText={item.text}
                score={item.score}
                sentiments={item.positive_keywords}
                socialMediaMentionType={item.social_media_mention_type}
                source={item.source}
                time={item.published_at}
                translatedText={item.translated}
                type={determineType(activeTitle)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
